import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Copy } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import {
  useGetAdminUnmappedPlans,
} from 'utils/web_api'
import { type AdminUnmappedPlan } from 'utils/web_api.schemas'

import CarrierNameFilter from './CarrierNameFilter'
import { EmployerRenewalStatusFilter } from './EmployerRenewalStatusFilter'
import StateFilter from './StateFilter'
import { usePlanTableFilters } from './usePlanTableFilters'
import CopyButtonWithAnimation from '../../reusable/CopyButtonWithAnimation'
import TanstackTable, { type PaginationMetadata } from '../../reusable/table/TanstackTable'

const PAGE_SIZE = 20

const columnHelper = createColumnHelper<AdminUnmappedPlan>()

const columns = [
  columnHelper.accessor('ancestor_plan_id', {
    header: 'Medical Plan ID',
    cell: ({ getValue }) => {
      const planId = getValue()
      return (
        <div className='flex items-center gap-2'>
          <span className='font-medium text-gray-900'>
            {planId?.slice(-6)}
          </span>
          {planId && (
            <CopyButtonWithAnimation content={planId}>
              <Copy size={16} className='text-gray-400 hover:text-blue-600 transition-colors' />
            </CopyButtonWithAnimation>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor('ancestor_plan_carrier_name', {
    header: 'Carrier Name',
  }),
  columnHelper.accessor('ancestor_plan_display_name', {
    header: 'Plan Name',
  }),
  columnHelper.accessor('ancestor_plan_hios_id', {
    header: 'HIOS ID',
  }),
  columnHelper.accessor('state', {
    header: 'State',
  }),
  columnHelper.accessor('member_count', {
    header: 'Members Enrolled',
  }),
]

interface UnmappedPlansFilters {
  states: string[]
  carrier_names: string[]
  renewed_employers_only: boolean
}

interface UnmappedPlansTableProps {
  pageParam?: number
}

const UnmappedPlansTable: React.FC<UnmappedPlansTableProps> = ({
  pageParam,
}) => {
  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: PAGE_SIZE,
  })

  const {
    filters,
    handleMultiSelectToggle,
    toggleBooleanFilter,
    clearFilters: clearFiltersBase,
    getActiveFilterCount,
  } = usePlanTableFilters<UnmappedPlansFilters>({
    initialFilters: {
      states: [],
      carrier_names: [],
      renewed_employers_only: false,
    },
    onFiltersChange: () => setPagination(prev => ({ ...prev, pageIndex: 1 })),
  })

  // Build query parameters with filters
  const queryParams = {
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    ...(filters.states.length > 0 && {
      'filters[states][]': filters.states,
    }),
    ...(filters.carrier_names.length > 0 && {
      'filters[carrier_names][]': filters.carrier_names,
    }),
    ...(filters.renewed_employers_only && {
      'filters[renewed_employers_only]': filters.renewed_employers_only,
    }),
  }

  const { data, isLoading, error } = useGetAdminUnmappedPlans(queryParams)

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  const clearFilters = () => {
    clearFiltersBase()
    setPagination(prev => ({ ...prev, pageIndex: 1 }))
  }

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.ancestor_plan_id,
    state: {
      pagination,
    },
  })

  return (
    <div className='space-y-4'>

      {/* Filters Section */}
      <div className='bg-white rounded-lg border shadow-sm p-4'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-medium text-gray-900'>Filters</h3>
          {getActiveFilterCount() > 0 && (
            <button
              onClick={clearFilters}
              className='text-sm text-blue-600 hover:text-blue-800 font-medium'
            >
              Clear all ({getActiveFilterCount()})
            </button>
          )}
        </div>

        <div className='flex flex-wrap gap-4 mb-4'>
          <StateFilter
            data-test-id='state-filter'
            selectedValues={filters.states}
            onSelectValue={(value) => handleMultiSelectToggle('states', value)}
          />

          <CarrierNameFilter
            selectedValues={filters.carrier_names}
            onSelectValue={(value) => handleMultiSelectToggle('carrier_names', value)}
          />
        </div>

        {/* Toggle Filters */}
        <div className='flex flex-col gap-2'>
          <EmployerRenewalStatusFilter
            renewedEmployersOnly={filters.renewed_employers_only}
            onToggle={() => toggleBooleanFilter('renewed_employers_only')}
          />
        </div>
      </div>

      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={Boolean(error)}
        noDataMessage='No unmapped plans found. All plans have required descendant mappings.'
        paginationLabelSingular='plan'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 1,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 1,
          })}
      />
    </div>
  )
}

export default UnmappedPlansTable
